/**
 * CommercialServicesPage — Iter-99 · Noble AI "View Services" destination.
 *
 * Standalone public page that lists every NECS service line across the
 * full commercial business (10 total). Each card shows:
 *   • NECS Service Line name
 *   • Tagline
 *   • Tier names only (Basic / Standard / Premium) — NO pricing anywhere
 *   • Frequency chip per tier
 *   • "Get a Quote" button routing to /contact with service_line + type + tier pre-filled
 *
 * NECS Custom renders a single "Build Your Package" card (no preset tiers).
 * NECS Valet is included per spec — no operational change, this is a
 * public-facing listing only. Valet Command Center + valet field staff
 * remain untouched.
 */
import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Sparkles, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import PublicPageNav from '../components/PublicPageNav';
import CommercialQuoteEngine from '../components/CommercialQuoteEngine';
import ServicesRunningCarousel from '../components/ServicesRunningCarousel';
import { COMMERCIAL_SERVICE_SLIDES } from '../lib/commercialServiceSlides';
import FacilityAssessmentForm from '../components/FacilityAssessmentForm';
import { INDUSTRIES } from '../lib/industryData';

const API = process.env.REACT_APP_BACKEND_URL;

const LINE_ICON = {
  residential: '🏢', office: '🏬', retail: '🛍️', gas_station: '⛽',
  school: '🏫', medical: '🏥', warehouse: '🏭',
  restaurant: '🍽️', mall: '🏦',
  // Iter-100 · Item 8 — Church / House of Worship replaces NECS Valet on
  // the General Commercial page. Uses a place-of-worship glyph.
  church: '⛪',
  // Iter-149 · Section 9/14-16 — Strategic Enterprise verticals.
  bank: '🏦', stadium: '🏟️', airport: '✈️',
  other: '📋',
};

export default function CommercialServicesPage() {
  const [lines, setLines] = useState([]);
  const [engineOpen, setEngineOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/commercial/service-lines?visibility=commercial`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return;
        setLines(d?.service_lines || []);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 500);
    }
  }, []);

  const gotoQuote = (line, tier) => {
    setEngineOpen(true);
    setTimeout(() => document.getElementById('quote-engine')?.scrollIntoView({ behavior: 'smooth' }), 150);
  };

  return (
    <div
      className="min-h-screen relative"
      style={{ backgroundColor: '#141414', color: '#e5e5e5' }}
      data-testid="commercial-services-page"
    >
      <PublicPageNav testIdPrefix="commercial-services" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        {/* Back to home */}
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest mb-6"
          style={{ color: '#C9A962' }}
          data-testid="commercial-services-back"
        >
          <ChevronLeft className="h-4 w-4" /> Back to Home
        </Link>

        {/* Iter-151 · Phase 4b — updated NECS Integrated Facility Care
            block. Removes the small "NECS Integrated Facility Care"
            intro cap (Phase 4a), replaces title/body with the exact
            spec text, adds the yellow One Facility. One Service Partner.
            One Integrated Solution. line, and removes the two CTAs
            (Phase 4c). */}
        <div className="text-center mb-10">
          <h1
            className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
            data-testid="commercial-services-heading"
          >
            INTEGRATED FACILITY CARE FOR MODERN <span style={{ color: '#C9A962' }}>COMMERCIAL ENVIRONMENTS</span>
          </h1>
          <p className="text-base sm:text-lg max-w-4xl mx-auto mb-4" style={{ color: '#a0a0a0', lineHeight: 1.7 }}>
            Compare facility-specific programs, then explore the priorities, service scope and scheduling needs for your industry.
          </p>
          <p
            className="text-lg font-semibold"
            style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em' }}
            data-testid="commercial-services-tagline"
          >
            One Facility. One Service Partner. One Integrated Solution.
          </p>
        </div>

        {loading ? (
          <p className="text-center text-sm text-zinc-500 py-10" data-testid="commercial-services-loading">
            Loading service lines…
          </p>
        ) : (
          <>
            {/* Iter-150 · Red Team Fix 6 — 12 Commercial Industries title. */}
            <div className="text-center mb-6" data-testid="commercial-services-count-header">
              <h2
                className="text-2xl md:text-3xl font-black text-white"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                12 <span style={{ color: '#C9A962' }}>COMMERCIAL INDUSTRIES</span>
              </h2>
              <p className="text-xs text-zinc-500 mt-2">
                9 Standard Commercial verticals · 3 Strategic Enterprise verticals
              </p>
            </div>
            {/* Iter-151 · Phase 3a — 4-per-row grid so Mall lands in the
                same row as the 3 Strategic Enterprise verticals
                (BankCare, StadiumCare, AirportCare). We sort commercial
                verticals into a canonical order (office → retail →
                gas_station → school → medical → warehouse → restaurant
                → church → mall → bank → stadium → airport) so Mall is
                position 9 (row 3, column 1) and Bank/Stadium/Airport
                fill columns 2/3/4 of that same row. */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="commercial-services-grid">
            {lines
              .slice()
              .sort((a, b) => {
                // Iter-151 · Phase 3a — canonical ordering: 8 standard,
                // then mall (position 9 · column 1 of row 3), then the
                // 3 Strategic Enterprise verticals (columns 2/3/4).
                const ORDER = ['office','retail','gas_station','school','medical','warehouse','restaurant','church','mall','bank','stadium','airport','other'];
                const ai = ORDER.indexOf(a.property_type);
                const bi = ORDER.indexOf(b.property_type);
                return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
              })
              .map((line) => {
              const isCustom = !!line.custom_only;
              return (
                <Card
                  key={line.property_type}
                  className="p-6 flex flex-col h-full transition-all hover:scale-[1.02]"
                  style={{ backgroundColor: '#1f1f1f', border: '2px solid #3f3f46' }}
                  data-testid={`commercial-service-card-${line.property_type}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p
                        className="text-[10px] uppercase tracking-widest mb-1 font-bold"
                        style={{ color: '#C9A962' }}
                        data-testid={`commercial-service-necs-name-${line.property_type}`}
                      >
                        {line.necs_name}
                      </p>
                      <p
                        className="text-lg font-bold text-white leading-tight"
                        style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                      >
                        {line.property_label}
                      </p>
                    </div>
                    <span className="text-3xl">{LINE_ICON[line.property_type] || '🏢'}</span>
                  </div>

                  <p className="text-xs text-zinc-400 mb-4 leading-snug flex-shrink-0">
                    {line.tagline}
                  </p>

                  {isCustom ? (
                    <div className="flex-1 flex flex-col justify-between">
                      <div
                        className="p-3 rounded-lg border mb-4"
                        style={{ borderColor: '#C9A962', backgroundColor: 'rgba(201,169,98,0.05)' }}
                      >
                        <p className="text-[11px] uppercase tracking-widest text-[#C9A962] font-bold mb-1">
                          Build Your Package
                        </p>
                        <p className="text-[12px] text-zinc-300 leading-snug">
                          Your NECS account manager designs the package around your property. No preset tiers.
                        </p>
                      </div>
                      <Button
                        onClick={() => gotoQuote(line, null)}
                        className="w-full font-bold flex items-center justify-center gap-1.5"
                        style={{ backgroundColor: '#C9A962', color: '#1a1a1a' }}
                        data-testid={`commercial-services-quote-btn-${line.property_type}`}
                      >
                        <Sparkles className="h-4 w-4" /> Get a Quote
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Tier name list — labels + frequency only, NO pricing */}
                      <div className="flex-1 space-y-2 mb-4">
                        {(line.tiers || []).map((tier) => (
                          <div
                            key={tier.key}
                            className="flex items-center justify-between px-3 py-2 rounded-md"
                            style={{ backgroundColor: '#0f0f0f', border: '1px solid #3f3f46' }}
                            data-testid={`commercial-services-tier-${line.property_type}-${tier.key}`}
                          >
                            <span
                              className="text-sm font-black uppercase tracking-wider"
                              style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif' }}
                            >
                              {tier.name}
                            </span>
                            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                              {tier.frequency}
                            </span>
                          </div>
                        ))}
                      </div>

                      <p className="text-[10px] text-zinc-500 italic text-center mb-3">
                        Final pricing confirmed by your NECS account manager.
                      </p>

                      <Button
                        onClick={() => gotoQuote(line, null)}
                        className="w-full font-bold flex items-center justify-center gap-1.5"
                        style={{ backgroundColor: '#C9A962', color: '#1a1a1a' }}
                        data-testid={`commercial-services-quote-btn-${line.property_type}`}
                      >
                        Get a Quote <ArrowRight className="h-4 w-4" />
                      </Button>
                      {line.disclaimer && (
                        <p className="text-[9px] italic text-center mt-2 text-zinc-500" data-testid={`commercial-disclaimer-${line.property_type}`}>{line.disclaimer}</p>
                      )}
                      {INDUSTRIES[line.property_type] && (
                        <Link
                          to={`/commercial-services/${line.property_type}`}
                          className="block text-center text-xs font-bold uppercase tracking-widest mt-3"
                          style={{ color: '#C9A962' }}
                          data-testid={`commercial-industry-link-${line.property_type}`}
                        >
                          Explore {line.necs_name} →
                        </Link>
                      )}
                    </>
                  )}
                </Card>
              );
            })}
          </div>
          </>
        )}

        {/* Iter-150 · Red Team Fix 6 — "SPECIALTY FACILITY SERVICES"
            packages block removed. Iter-151 · Phase 3b replaces it with
            the running services carousel. */}
        <ServicesRunningCarousel testId="commercial-services-carousel" slides={COMMERCIAL_SERVICE_SLIDES} />

        {/* E16 — Commercial Quote Engine */}
        <section id="quote-engine" className="mt-16" data-testid="quote-engine-section">
          <h2 className="text-2xl md:text-3xl font-black text-white text-center mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            BUILD MY <span style={{ color: '#C9A962' }}>SERVICE PROGRAM</span>
          </h2>
          <p className="text-sm text-zinc-400 text-center max-w-2xl mx-auto mb-8">
            Tell us about your facility and see a preliminary estimate range with a recommended care tier.
          </p>
          {engineOpen ? (
            <CommercialQuoteEngine />
          ) : (
            <div className="text-center">
              <Button
                className="font-black uppercase tracking-widest px-10 py-6 text-base"
                style={{ backgroundColor: '#C9A962', color: '#1a1a1a', fontFamily: 'Barlow Condensed, sans-serif' }}
                onClick={() => setEngineOpen(true)}
                data-testid="reveal-cs-engine-btn"
              >
                GET A QUOTE
              </Button>
            </div>
          )}
        </section>

        {/* E17 — Facility Assessment */}
        <section id="assessment" className="mt-16" data-testid="assessment-section">
          <h2 className="text-2xl md:text-3xl font-black text-white text-center mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            NECS FACILITY <span style={{ color: '#C9A962' }}>ASSESSMENT</span>
          </h2>
          <p className="text-sm text-zinc-400 text-center max-w-2xl mx-auto mb-8">
            Request an on-site walkthrough — we capture your full facility profile and build a complete proposal.
          </p>
          <FacilityAssessmentForm />
        </section>

        {/* E29 — Final CTA */}
        <section className="mt-16 text-center rounded-xl p-8" style={{ backgroundColor: '#1f1f1f', border: '1px solid #C9A962' }} data-testid="commercial-final-cta">
          <h2 className="text-2xl font-black text-white uppercase mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            READY TO ELEVATE YOUR <span style={{ color: '#C9A962' }}>FACILITY?</span>
          </h2>
          <p className="text-sm text-zinc-400 max-w-2xl mx-auto mb-6">
            Tell NECS about your property, business or facility. We'll help develop a service program based on your operational requirements, facility size and service priorities.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button style={{ backgroundColor: '#C9A962', color: '#1a1a1a' }} className="font-bold uppercase tracking-widest" onClick={() => { setEngineOpen(true); setTimeout(() => document.getElementById('quote-engine')?.scrollIntoView({ behavior: 'smooth' }), 150); }} data-testid="commercial-final-quote">GET A QUOTE</Button>
            <Button style={{ backgroundColor: 'transparent', color: '#C9A962', border: '2px solid #C9A962' }} className="font-bold uppercase tracking-widest" onClick={() => document.getElementById('assessment')?.scrollIntoView({ behavior: 'smooth' })} data-testid="commercial-final-assessment">REQUEST A FACILITY ASSESSMENT</Button>
            <Button style={{ backgroundColor: 'transparent', color: '#C9A962', border: '2px solid #C9A962' }} className="font-bold uppercase tracking-widest" onClick={() => window.dispatchEvent(new CustomEvent('necs:open-support-chat', { detail: { intent: 'commercial_quote' } }))} data-testid="commercial-final-sales">TALK TO SALES</Button>
          </div>
        </section>
      </main>
    </div>
  );
}
